import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MessageSettings } from '@anduin/api';
import { applicationDebounceTime, validateEmailList } from '@anduin/shared';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, startWith, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-collections-addresses',
  templateUrl: './collections-addresses.component.html',
  styleUrls: ['./collections-addresses.component.scss'],
  standalone: false,
})
export class CollectionsAddressesComponent implements OnInit, OnDestroy {
  private _includeCcAndBcc$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  includeCcAndBcc$: Observable<boolean> = this._includeCcAndBcc$.asObservable();
  @Input()
  set includeCcAndBcc(value: boolean) {
    this._includeCcAndBcc$.next(value);
  }

  @Input()
  set fromName(value: string) {
    this.fromNameControl.setValue(value, { emitEvent: false });
  }

  @Output()
  fromNameChange = new EventEmitter<string>();

  @Input()
  set replyToAddress(value: string) {
    this.replyToAddressControl.setValue(value, { emitEvent: false });
  }

  @Output()
  replyToAddressChange = new EventEmitter<string>();

  @Input()
  set ccRecipients(recipients: string[]) {
    if (recipients && Array.isArray(recipients)) {
      this.ccRecipientsControl.setValue(recipients.join(', '), { emitEvent: false });
    }
  }

  @Output()
  ccRecipientsChange = new EventEmitter<string[]>();

  @Input()
  set bccRecipients(recipients: string[]) {
    if (recipients && Array.isArray(recipients)) {
      this.bccRecipientsControl.setValue(recipients.join(', '), { emitEvent: false });
    }
  }

  @Output()
  bccRecipientsChange = new EventEmitter<string[]>();

  private _disabled = false;

  @Input()
  set disabled(value: boolean) {
    this._disabled = value;
    if (value) {
      this.addressesForm.disable();
    } else {
      this.addressesForm.enable();
    }
  }

  get disabled(): boolean {
    return this._disabled;
  }

  @Output()
  isValid: EventEmitter<boolean> = new EventEmitter<boolean>();

  unsubscribe$: Subject<void> = new Subject<void>();

  addressesForm: UntypedFormGroup = this.fb.group({
    fromName: this.fb.control(null, { updateOn: 'change' }),
    replyToAddress: this.fb.control(null, { validators: [Validators.required, validateEmailList()], updateOn: 'change' }),
    ccRecipients: this.fb.control(null, { validators: validateEmailList(), updateOn: 'change' }),
    bccRecipients: this.fb.control(null, { validators: validateEmailList(), updateOn: 'change' }),
  });

  get fromNameControl(): UntypedFormControl {
    return this.addressesForm.controls['fromName'] as UntypedFormControl;
  }

  get replyToAddressControl(): UntypedFormControl {
    return this.addressesForm.controls['replyToAddress'] as UntypedFormControl;
  }

  get ccRecipientsControl(): UntypedFormControl {
    return this.addressesForm.controls['ccRecipients'] as UntypedFormControl;
  }

  get bccRecipientsControl(): UntypedFormControl {
    return this.addressesForm.controls['bccRecipients'] as UntypedFormControl;
  }

  constructor(private fb: UntypedFormBuilder) {}

  ngOnInit(): void {
    this.fromNameControl.valueChanges
      .pipe(
        applicationDebounceTime(),
        tap((value) => {
          this.fromNameChange.emit(value);
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe();

    this.replyToAddressControl.valueChanges
      .pipe(
        applicationDebounceTime(),
        tap((value) => {
          this.replyToAddressChange.emit(value);
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe();

    this.ccRecipientsControl.valueChanges
      .pipe(
        map((value) => (value ? value.split(',').map((address) => address.trim()) : [])),
        tap((value) => {
          this.ccRecipientsChange.emit(value);
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe();

    this.bccRecipientsControl.valueChanges
      .pipe(
        map((value) => (value ? value.split(',').map((address) => address.trim()) : [])),
        tap((value) => {
          this.bccRecipientsChange.emit(value);
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe();

    combineLatest([
      this._includeCcAndBcc$,
      this.ccRecipientsControl.valueChanges.pipe(
        map((value) => this.ccRecipientsControl.valid),
        startWith(true),
      ),
      this.bccRecipientsControl.valueChanges.pipe(
        map((value) => this.bccRecipientsControl.valid),
        startWith(true),
      ),
      this.fromNameControl.valueChanges.pipe(
        map((value) => this.fromNameControl.valid),
        startWith(true),
      ),
      this.replyToAddressControl.valueChanges.pipe(
        map((value) => this.replyToAddressControl.valid),
        startWith(true),
      ),
    ])
      .pipe(
        map(([includeCcAndBcc, ccValid, bccValid, ...addressControlValidities]) =>
          // determine if cc and bcc need to be included in the validation
          (includeCcAndBcc ? [ccValid, bccValid, ...addressControlValidities] : addressControlValidities).every((isValid) => isValid),
        ),
        distinctUntilChanged(),
        tap((isValid) => this.isValid.emit(isValid)),
        takeUntil(this.unsubscribe$),
      )
      .subscribe();
  }

  markAllAsTouched(): void {
    this.addressesForm.markAllAsTouched();
  }

  reset(messageSettings: MessageSettings, ccList: string[], bccList: string[]): void {
    this.fromName = messageSettings.fromName;
    this.replyToAddress = messageSettings.from;
    this.ccRecipients = ccList;
    this.bccRecipients = bccList;
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
  }
}
